/**
 * Payment Data Export Utilities
 * Generate CSV exports of payment data for offline backup
 */

import { format } from 'date-fns';

/**
 * Escape CSV field (wrap in quotes if contains comma, quote, or newline)
 */
const escapeCSV = (value) => {
  if (value == null || value === '') return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Format date for CSV output
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return format(new Date(dateStr), 'yyyy-MM-dd HH:mm');
  } catch {
    return dateStr;
  }
};

/**
 * Format date (short) for CSV output
 */
const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  try {
    return format(new Date(dateStr), 'yyyy-MM-dd');
  } catch {
    return dateStr;
  }
};

/**
 * Build transactions.csv - every payment record
 * @param {Array} payments - Array of payment ledger entries with allocations
 * @returns {string} CSV content
 */
export const buildTransactionsCSV = (payments, allocationsMap) => {
  const headers = [
    'Payment ID',
    'Date',
    'Payer Name',
    'Amount',
    'Currency',
    'Payment Method',
    'Source',
    'Sessions Covered',
    'Total Applied',
    'Note',
    'Entered By',
    'Voided',
    'Voided At',
    'Voided By'
  ];

  const rows = payments.map(payment => {
    const allocations = allocationsMap[payment.id] || [];
    const sessionsCovered = allocations
      .map(a => {
        const sessionName = a.session_player?.sessions?.name || 'Unknown';
        const sessionDate = formatDateShort(a.session_player?.sessions?.session_date);
        return `${sessionName} (${sessionDate})`;
      })
      .join('; ');
    
    const totalApplied = allocations.reduce((sum, a) => sum + parseFloat(a.amount_applied || 0), 0);

    const payerName = payment.payer 
      ? `${payment.payer.first_name} ${payment.payer.last_name}`
      : '';
    
    const enteredBy = payment.created_by_player
      ? `${payment.created_by_player.first_name} ${payment.created_by_player.last_name}`
      : '';
    
    const voidedBy = payment.deleted_by_player
      ? `${payment.deleted_by_player.first_name} ${payment.deleted_by_player.last_name}`
      : '';

    const paymentMethodLabel = {
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'other': 'Other'
    }[payment.payment_method] || payment.payment_method || '';

    const sourceLabel = {
      'manual_session_toggle': 'Manual',
      'lump_payment': 'Recorded',
      'legacy_migration': 'Legacy'
    }[payment.payment_source] || payment.payment_source || '';

    return [
      payment.id,
      formatDate(payment.created_at),
      payerName,
      payment.amount,
      payment.currency_code,
      paymentMethodLabel,
      sourceLabel,
      sessionsCovered,
      totalApplied.toFixed(2),
      payment.note || '',
      enteredBy,
      payment.is_deleted ? 'Yes' : 'No',
      payment.is_deleted ? formatDate(payment.deleted_at) : '',
      payment.is_deleted ? voidedBy : ''
    ].map(escapeCSV);
  });

  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Build player_balances.csv - summary per player
 * @param {Array} playerOwed - Array of players with outstanding payments
 * @param {Object} playerBalances - Map of playerId to balance info
 * @returns {string} CSV content
 */
export const buildPlayerBalancesCSV = (playerOwed, playerBalances) => {
  const headers = [
    'Player Name',
    'Membership Status',
    'Amount Owed',
    'Currency',
    'Credit Balance',
    'Net Balance',
    'Unpaid Sessions Count',
    'Unpaid Session Dates'
  ];

  const rows = playerOwed.map(player => {
    const balance = playerBalances[player.playerId];
    
    // Sum all amounts owed across currencies (if multiple, use first)
    const owedEntries = Object.entries(player.owedByCurrency || {});
    const totalOwed = owedEntries.reduce((sum, [_, amount]) => sum + amount, 0);
    const currency = owedEntries[0]?.[0] || balance?.currencyCode || 'GBP';

    const unpaidDates = (player.sessions || [])
      .map(s => formatDateShort(s.sessionDate))
      .join('; ');

    const statusLabel = {
      'owner': 'Owner',
      'admin': 'Admin',
      'host': 'Host',
      'member': 'Member',
      'guest': 'Guest'
    }[player.membershipStatus] || player.membershipStatus || 'Guest';

    return [
      player.playerName,
      statusLabel,
      totalOwed.toFixed(2),
      currency,
      balance?.credit?.toFixed(2) || '0.00',
      balance?.netBalance?.toFixed(2) || (0 - totalOwed).toFixed(2),
      player.unpaidSessionsCount || 0,
      unpaidDates
    ].map(escapeCSV);
  });

  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Build sessions_outstanding.csv - sessions with unpaid players
 * @param {Array} sessionsWithOutstanding - Array of sessions with outstanding amounts
 * @returns {string} CSV content
 */
export const buildSessionsCSV = (sessionsWithOutstanding) => {
  const headers = [
    'Session Name',
    'Date',
    'Status',
    'Price Per Player',
    'Currency',
    'Unpaid Players',
    'Outstanding Amount'
  ];

  const rows = sessionsWithOutstanding.map(session => {
    return [
      session.name || 'Unnamed Session',
      formatDateShort(session.session_date),
      session.session_status,
      session.price_per_player?.toFixed(2) || '0.00',
      session.currency_code || 'GBP',
      session.unpaidPlayersCount || 0,
      session.outstandingAmount?.toFixed(2) || '0.00'
    ].map(escapeCSV);
  });

  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Build allocations.csv - detailed audit trail
 * @param {Array} payments - Array of payment ledger entries
 * @param {Object} allocationsMap - Map of paymentId to allocations array
 * @returns {string} CSV content
 */
export const buildAllocationsCSV = (payments, allocationsMap) => {
  const headers = [
    'Allocation ID',
    'Payment ID',
    'Payer Name',
    'Session Name',
    'Session Date',
    'Amount Applied',
    'Currency',
    'Payment Date'
  ];

  const rows = [];
  
  payments.forEach(payment => {
    const allocations = allocationsMap[payment.id] || [];
    const payerName = payment.payer
      ? `${payment.payer.first_name} ${payment.payer.last_name}`
      : '';
    
    allocations.forEach(allocation => {
      rows.push([
        allocation.id,
        allocation.payment_id,
        payerName,
        allocation.session_player?.sessions?.name || 'Unknown',
        formatDateShort(allocation.session_player?.sessions?.session_date),
        parseFloat(allocation.amount_applied || 0).toFixed(2),
        payment.currency_code,
        formatDate(payment.created_at)
      ].map(escapeCSV));
    });
  });

  const csvContent = [
    headers.map(escapeCSV).join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Trigger CSV download in browser
 * @param {string} filename - Name of file (without path)
 * @param {string} csvContent - CSV content as string
 */
export const downloadCSV = (filename, csvContent) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Sleep helper for sequential downloads
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Export all payment data as 4 CSV files
 * @param {Object} params
 * @param {Array} params.payments - Payment ledger entries
 * @param {Object} params.allocationsMap - Map of paymentId to allocations
 * @param {Array} params.playerOwed - Players with outstanding payments
 * @param {Object} params.playerBalances - Map of playerId to balance info
 * @param {Array} params.sessionsWithOutstanding - Sessions with unpaid players
 * @param {string} params.clubName - Club name for filename prefix
 */
export const exportAllPaymentData = async ({
  payments,
  allocationsMap,
  playerOwed,
  playerBalances,
  sessionsWithOutstanding,
  clubName
}) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const prefix = clubName ? clubName.toLowerCase().replace(/[^a-z0-9]/g, '_') : 'shuttlr';

  // Build all CSVs
  const transactionsCSV = buildTransactionsCSV(payments, allocationsMap);
  const playerBalancesCSV = buildPlayerBalancesCSV(playerOwed, playerBalances);
  const sessionsCSV = buildSessionsCSV(sessionsWithOutstanding);
  const allocationsCSV = buildAllocationsCSV(payments, allocationsMap);

  // Download sequentially with delays to avoid browser blocking
  downloadCSV(`${prefix}_transactions_${today}.csv`, transactionsCSV);
  await sleep(200);
  
  downloadCSV(`${prefix}_player_balances_${today}.csv`, playerBalancesCSV);
  await sleep(200);
  
  downloadCSV(`${prefix}_sessions_outstanding_${today}.csv`, sessionsCSV);
  await sleep(200);
  
  downloadCSV(`${prefix}_allocations_${today}.csv`, allocationsCSV);
};
